import dayjs from 'dayjs';
import { dayjsInZone } from './timezone';

// TTLock / Control Gateway mobile action types (have explicit actionType)
export const MOBILE_ACTION_TYPES = new Set(['Bluetooth Unlock', 'Gateway Unlock']);
export const PASSCODE_ACTION_TYPES = new Set(['Passcode Unlock', 'Invalid Passcode']);

// actionResult values for a granted/opened event (vs a denial); mirrors AccessLogItem's SUCCESSFUL_RESULTS
// so the dashboard cards classify like the feed.
export const SUCCESSFUL_ACTION_RESULTS = new Set([
	'UserGrantedIn',
	'DoorUnlock',
	'DoorTimedUnlock',
	'DoorLock',
	'InvokeOperatorAction',
	'success',
	'GRANTED'
]);

// 🛑 A relative preset's window, computed AT CALL TIME so a tab left open overnight moves on; undefined
// for `custom` (and unknown), since a range the user picked is never re-based.
export const datePresetRange = (preset) => {
	const ranges = {
		'today': () => ({ start: dayjs().startOf('day'), end: dayjs().endOf('day') }),
		'7d': () => ({ start: dayjs().subtract(6, 'day').startOf('day'), end: dayjs().endOf('day') }),
		'30d': () => ({ start: dayjs().subtract(29, 'day').startOf('day'), end: dayjs().endOf('day') })
	};
	const range = ranges[preset]?.();
	return range ? { startDate: range.start.toISOString(), endDate: range.end.toISOString() } : undefined;
};

const PRESET_LABEL = { 'today': 'today', '7d': 'last 7 days', '30d': 'last 30 days' };

// A date filter in words ('last 7 days', '1 Sep – 4 Sep'); undefined for an incomplete or unknown one.
export const datePresetLabel = (preset, startDate, endDate) =>
	preset === 'custom'
		? startDate && endDate
			? `${dayjs(startDate).format('D MMM')} – ${dayjs(endDate).format('D MMM')}`
			: undefined
		: PRESET_LABEL[preset];

// Buckets newest-first logs into relative time groups (AccessLogCard and the drawer Activity tabs share the
// headers); empty buckets are dropped. `tz` anchors "today"/"yesterday" to the org's day, not the viewer's.
export function groupByTime(logs, tz) {
	const now = dayjsInZone(undefined, tz);
	const todayStart = now.startOf('day');
	const yesterdayStart = todayStart.subtract(1, 'day');
	const oneHourAgo = now.subtract(1, 'hour');

	const buckets = [
		{ key: 'last-hour', label: 'Last hour', logs: [] },
		{ key: 'earlier-today', label: 'Earlier today', logs: [] },
		{ key: 'yesterday', label: 'Yesterday', logs: [] },
		{ key: 'earlier', label: 'Earlier', logs: [] }
	];

	for (const log of logs) {
		const t = dayjs(log.eventAt);
		if (t.isAfter(oneHourAgo)) buckets[0].logs.push(log);
		else if (t.isAfter(todayStart)) buckets[1].logs.push(log);
		else if (t.isAfter(yesterdayStart)) buckets[2].logs.push(log);
		else buckets[3].logs.push(log);
	}

	return buckets.filter((b) => b.logs.length > 0);
}

// IR system/operator events that carry no credentialID but are NOT person-initiated
const SYSTEM_ACTION_TYPES = new Set(['DoorLock', 'InvokeOperatorAction']);

// Returns 'mobile' | 'card' | 'pin' | 'unknown'
export function classifyCredentialType(actionType, credentialID) {
	if (MOBILE_ACTION_TYPES.has(actionType)) return 'mobile';
	if (PASSCODE_ACTION_TYPES.has(actionType)) return 'pin';
	if (actionType === 'IC Card Unlock') return 'card';
	if (credentialID != null) return 'card';
	// No credentialID and no explicit type → IR mobile (person admitted via app, no physical credential)
	if (!SYSTEM_ACTION_TYPES.has(actionType)) return 'mobile';
	return 'unknown';
}

// Labels for a platform-issued command, by the client that sent it.
export const CLOUD_ACCESS = 'Cloud Access';
export const APP_ACCESS = 'App Access';

// `platform` is the SERVER's record of which client sent a command — it is the only side
// that knows. 'dashboard' is the web dashboard; 'app' is the mobile app.
const DASHBOARD_PLATFORM = 'dashboard';
const PLATFORM_METHOD = { [DASHBOARD_PLATFORM]: CLOUD_ACCESS, app: APP_ACCESS };

// Method label for a platform-issued command, from `platform` only ('Cloud Access' = the dashboard sent it);
// a row without one predates the field and reads 'Remote Unlock'.
export const platformMethodLabel = (log) => PLATFORM_METHOD[log.platform] || 'Remote Unlock';

// { mobile, [primaryCredentialID] } → each key's newest log, null when none (undefined = not loaded yet);
// mobile covers TTLock/Gateway actionTypes and Inner Range grants with no credentialID.
export function buildLastLogMap(logs, credentialRows) {
	const map = {};

	for (const log of logs) {
		if (MOBILE_ACTION_TYPES.has(log.actionType)) {
			if (!('mobile' in map)) map['mobile'] = log;
		} else if (log.credentialID != null) {
			const matched = credentialRows.find((cred) => {
				const allIDs = new Set([cred.credentialID, ...(cred.integrations || []).map((i) => i.credentialID)]);
				return allIDs.has(log.credentialID);
			});
			if (matched && !(matched.credentialID in map)) {
				map[matched.credentialID] = log;
			}
		} else if (!SYSTEM_ACTION_TYPES.has(log.actionType)) {
			// credentialID is null and not a system event → IR mobile access
			if (!('mobile' in map)) map['mobile'] = log;
		}
	}

	if (!('mobile' in map)) map['mobile'] = null;
	for (const cred of credentialRows) {
		if (!(cred.credentialID in map)) map[cred.credentialID] = null;
	}

	return map;
}

// The server's `outcome` is canonical: its rule also covers Cloud Key grants and
// IR exits, which SUCCESSFUL_ACTION_RESULTS misses. The set is the older fallback.
export const isSuccessfulAccess = (log) =>
	log?.outcome ? log.outcome === 'success' : SUCCESSFUL_ACTION_RESULTS.has(log?.actionResult);

// Newest access as "<method> - <date>", for a person or locker summary tile.
// Moved here from bookingHelpers: it reads access logs, not bookings.
export const getLastUnlocked = (logs) => {
	if (!logs?.length) return '-';

	const [newest] = logs.filter((log) => log.methodLabel).sort((a, b) => new Date(b.eventAt) - new Date(a.eventAt));

	return newest ? `${newest.methodLabel} - ${dayjs(newest.eventAt).format('D/MM/YY')}` : '-';
};
